export interface OAuthAuthorizeCallbackContext {
    redirect_uri: string | null;
    fallback_redirect_uri?: string | null;
}

export type OAuthAuthorizeCallbackTarget = 'auto' | 'redirect' | 'fallback';

export interface OAuthAuthorizeCallbackResponse {
    code?: string;
    state?: string;
    redirect_url?: string;
    redirectUrl?: string;
    url?: string;
    data?: OAuthAuthorizeCallbackResponse | null;
    message?: string;
    status?: string;
    [key: string]: any;
}

export const buildOAuthCallbackUrl = (
    baseUrl: string | null | undefined,
    params: {
        code?: string | null;
        state?: string | null;
    }
): string | null => {
    const targetUrl = baseUrl?.trim();
    const code = params.code?.trim();
    const state = params.state?.trim();

    if (!targetUrl || !code || !state) {
        return null;
    }

    try {
        const callbackUrl = new URL(targetUrl);
        callbackUrl.searchParams.set('code', code);
        callbackUrl.searchParams.set('state', state);
        return callbackUrl.toString();
    } catch (error) {
        console.warn('[buildOAuthCallbackUrl] Failed to construct OAuth callback URL:', {
            targetUrl,
            error,
        });
        return null;
    }
};

export const resolveOAuthAuthorizeCallbackUrl = (
    oauthContext: OAuthAuthorizeCallbackContext,
    responseData: OAuthAuthorizeCallbackResponse | null | undefined,
    locationHeader?: string | null,
    preferredTarget: OAuthAuthorizeCallbackTarget = 'auto',
): string | null => {
    const directRedirectUrl =
        locationHeader?.trim()
        || responseData?.redirect_url
        || responseData?.redirectUrl
        || responseData?.url
        || responseData?.data?.redirect_url
        || responseData?.data?.redirectUrl
        || responseData?.data?.url
        || null;

    const payload = responseData?.data && typeof responseData.data === 'object'
        ? responseData.data
        : responseData;
    let directRedirectCode: string | null = null;
    let directRedirectState: string | null = null;
    if (directRedirectUrl) {
        try {
            const parsedDirectRedirect = new URL(directRedirectUrl);
            directRedirectCode = parsedDirectRedirect.searchParams.get('code');
            directRedirectState = parsedDirectRedirect.searchParams.get('state');
        } catch {
            // Preserve legacy direct redirects even when they are not parseable URLs.
        }
    }
    const code = payload?.code ?? directRedirectCode;
    const state = payload?.state ?? directRedirectState;
    const candidateBaseUrls = preferredTarget === 'fallback'
        ? [oauthContext.fallback_redirect_uri || null, oauthContext.redirect_uri || null]
        : preferredTarget === 'redirect'
            ? [oauthContext.redirect_uri || null, oauthContext.fallback_redirect_uri || null]
            : [oauthContext.redirect_uri || null, oauthContext.fallback_redirect_uri || null];

    // Explicit callers own the handoff transport. Rebuild a backend-provided
    // direct redirect onto the requested callback.
    if (preferredTarget !== 'auto') {
        for (const candidateBaseUrl of candidateBaseUrls) {
            const callbackUrl = buildOAuthCallbackUrl(candidateBaseUrl, { code, state });
            if (callbackUrl) {
                return callbackUrl;
            }
        }
    }

    if (directRedirectUrl) {
        return directRedirectUrl;
    }

    for (const candidateBaseUrl of candidateBaseUrls) {
        const callbackUrl = buildOAuthCallbackUrl(candidateBaseUrl, { code, state });
        if (callbackUrl) {
            return callbackUrl;
        }
    }

    return null;
};
